import React, { useState, useMemo } from 'react';
import { DevAccessLink } from '../../../feedback';
import { TopBar } from '../chrome/TopBar';
import { SectionHeader } from '../primitives/SectionHeader';
import { WizardHatIcon, SpellbookIcon, BrandIcon } from '../primitives/icons';
import { ActiveCharacterCard } from './ActiveCharacterCard';
import { CampaignCard } from './CampaignCard';
import { TableNote } from './TableNote';
import { SoloCharCard } from './SoloCharCard';
import { NewCharCard } from './NewCharCard';
import { GetStartedRow } from './GetStartedRow';
import { DemoCampaignCard } from './DemoCampaignCard';
import { DEFAULT_DEMO_PLAYER_CHAR_ID, DEMO_CAMPAIGN_ID } from '../../../../demoCampaign';

// First-impression demo character. Shown in the banner + solo grid when the
// user has no campaigns, no solo characters, and no resolved active character.
// As soon as they create anything real, this synthetic row falls out of the
// view (isEmpty becomes false) and the banner switches to their own data.
const DEMO_LIKA = {
  id: DEFAULT_DEMO_PLAYER_CHAR_ID,
  charId: DEFAULT_DEMO_PLAYER_CHAR_ID,
  name: "Li'ka",
  race: 'Half-Elf',
  charClass: 'Cleric',
  cls: 'L5 Cleric',
  level: 5,
  campaignId: DEMO_CAMPAIGN_ID,
  hp: 24, maxHp: 42,
  mp: 0, maxMp: 0,
  paletteIndex: 0,
  isDemo: true,
};

export const HomeView = ({
  theme, density, palettes,
  cardStyle, cardFinish,
  onOpenThemes,
  bgColor, setBgColor, themeBgColor,
  onEnterCampaign,
  data,
  actions,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};

  const user = data?.user || { name: '', handle: '@you', initial: '?' };
  const rawActiveCharacter = data?.activeCharacter || null;
  const campaigns = useMemo(() => data?.campaigns || [], [data?.campaigns]);
  const realCampaigns = useMemo(
    () => campaigns.filter((campaign) => !campaign.isDemo),
    [campaigns],
  );
  const tableNotes = data?.tableNotes || [];
  const rawSoloCharacters = data?.soloCharacters || [];

  // Empty-state demo injection: when the user has nothing of their own,
  // surface Li'ka as a demo character in both the banner and the solo grid
  // so the dashboard never feels empty on first visit. The synthetic row
  // carries isDemo so downstream cards render "Try demo" + a DEMO tag.
  const isEmpty = !rawActiveCharacter
    && realCampaigns.length === 0
    && rawSoloCharacters.length === 0;
  const activeCharacter = rawActiveCharacter || (isEmpty ? DEMO_LIKA : null);
  const soloCharacters = isEmpty ? [DEMO_LIKA] : rawSoloCharacters;

  // Tracks which campaign card is currently hovered, so the others can dim.
  // null = none hovered; otherwise the campaign id.
  const [hoveredCampaignId, setHoveredCampaignId] = useState(null);
  // Same pattern for solo characters.
  const [hoveredSoloId, setHoveredSoloId] = useState(null);
  // When the demo campaign card is hovered, all real campaigns dim — it's
  // pulling focus toward "new here?", so the existing campaigns recede.
  const [demoHovered, setDemoHovered] = useState(false);

  // Look up the campaign that hosts the active character so the hero card
  // can borrow its palette + name. Falls back to the first slot if the
  // active character has no resolved campaign yet.
  const activeCampaign = activeCharacter
    ? campaigns.find(camp => camp.id === activeCharacter.campaignId)
    : null;
  const activePalette = palettes[activeCampaign?.paletteIndex ?? 0];
  const campaignsById = useMemo(() => {
    const map = {};
    campaigns.forEach(camp => { map[camp.id] = camp; });
    return map;
  }, [campaigns]);

  // Build notes with attached palette + campaign name. Empty until the
  // announcements backend lands in Phase C.
  const enrichedNotes = tableNotes.map(n => {
    const camp = campaignsById[n.campaignId];
    return {
      ...n,
      campaignName: camp?.name || '',
      palette: palettes[camp?.paletteIndex ?? 0],
    };
  });
  const getCampaignEnterCharId = (camp) => {
    if (!camp) return null;
    if (camp.myCharacterId) return camp.myCharacterId;
    if (camp.yourCharId) return camp.yourCharId;
    if (camp.myCharacter?.charId) return camp.myCharacter.charId;
    const campaignId = camp.id || camp.campaignId;
    if (activeCharacter?.campaignId && campaignId && activeCharacter.campaignId === campaignId) {
      return activeCharacter.charId || activeCharacter.id || null;
    }
    const loadedMine = (data?.campaignCharacters?.[camp.id] || [])
      .find((ch) => ch.isMine && ch.isPlayable !== false);
    return loadedMine?.charId || loadedMine?.id || null;
  };
  const handleCampaignEnter = (camp) => {
    if (camp.role === 'owner' || camp.role === 'dm') {
      actions?.onEnterAsCampaignDm?.(camp.id);
      return;
    }
    const charId = getCampaignEnterCharId(camp);
    if (charId) {
      actions?.onEnterAsCampaignChar?.(camp.id, charId);
      return;
    }
    onEnterCampaign && onEnterCampaign(camp.id);
  };

  // Section panels are open regions, not nested cards. They have a subtle
  // top-stroke separator and breathing room, but no surrounding chrome. This
  // lets the inner cards (campaigns, solo chars, notes) lift cleanly against
  // the page background instead of competing with a wrapper card.
  const sectionRegionStyle = {
    paddingTop: density.spacing.sectionGap,
    paddingBottom: density.spacing.sectionGap,
    marginBottom: density.spacing.cardGap,
    borderTop: `1px solid ${c.borderLight}`,
  };

  // First region on the page doesn't get the top border
  const firstSectionRegionStyle = {
    ...sectionRegionStyle,
    borderTop: 'none',
    paddingTop: 0,
  };

  return (
    <>
      <TopBar
        theme={theme}
        density={density}
        user={user}
        onOpenThemes={onOpenThemes}
        bgColor={bgColor}
        setBgColor={setBgColor}
        themeBgColor={themeBgColor}

      />

      {activeCharacter && (
        <ActiveCharacterCard
          theme={theme}
          density={density}
          char={activeCharacter}
          palette={activePalette}
          cardStyle={cardStyle}
          cardFinish={cardFinish}
          campaignName={activeCampaign?.name || ''}
          isDemo={!!activeCharacter.isDemo}
          onContinue={() => {
            if (activeCharacter.isDemo) {
              actions?.onEnterDemoCampaign?.({
                role: 'player',
                charId: activeCharacter.charId || activeCharacter.id || DEFAULT_DEMO_PLAYER_CHAR_ID,
              });
              return;
            }
            if (actions?.onEnterAsCampaignChar && activeCharacter.campaignId) {
              actions.onEnterAsCampaignChar(activeCharacter.campaignId, activeCharacter.charId || activeCharacter.id);
              return;
            }
            actions?.onEnterAsStandaloneChar?.(activeCharacter.charId || activeCharacter.id);
          }}
        />
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
        rowGap: density.spacing.cardGap,
        columnGap: 28,
      }}>
        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader icon="✦" title="My Campaigns" count={campaigns.length} theme={theme} density={density} />
            {campaigns.length === 0 ? (
              <>
                <div style={{
                  fontSize: '0.84rem',
                  color: c.textMuted,
                  fontFamily: t.fontFamily,
                  lineHeight: 1.5,
                  margin: '4px 0 12px',
                  fontStyle: 'italic',
                }}>
                  You're not in any campaigns yet. Start one as DM or join with an invite code.
                </div>
                <GetStartedRow
                  theme={theme} density={density}
                  icon={<BrandIcon size={34} />}
                  title="Start a campaign as DM"
                  body="You own it; invite players when you're ready"
                  palette={palettes[0]}
                  onClick={() => actions?.onOpenCreateCampaign?.()}
                />
                <GetStartedRow
                  theme={theme} density={density}
                  icon={<SpellbookIcon size={34} />}
                  title="Join a campaign"
                  body="Enter an invite code from your DM"
                  palette={palettes[1]}
                  onClick={() => actions?.onOpenJoinCampaign?.()}
                />
              </>
            ) : (
              campaigns.map(camp => (
                <CampaignCard
                  key={camp.id}
                  theme={theme}
                  density={density}
                  camp={camp}
                  palette={palettes[camp.paletteIndex]}
                  cardStyle={cardStyle}
                  cardFinish={cardFinish}
                  dimmed={
                    demoHovered ||
                    (hoveredCampaignId !== null && hoveredCampaignId !== camp.id)
                  }
                  onHoverChange={(isHovered) => {
                    if (isHovered) {
                      setHoveredCampaignId(camp.id);
                    } else {
                      setHoveredCampaignId(prev => prev === camp.id ? null : prev);
                    }
                  }}
                  onClick={() => onEnterCampaign && onEnterCampaign(camp.id)}
                  onEnter={() => handleCampaignEnter(camp)}
                />
              ))
            )}
          </div>

          <div style={sectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="Solo Characters"
              count={soloCharacters.length}
              theme={theme}
              density={density}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${density.layout.gridMinWidth || '200px'}, 1fr))`,
              gap: density.spacing.itemGap,
            }}>
              {soloCharacters.map(ch => (
                <SoloCharCard
                  key={ch.id}
                  theme={theme}
                  density={density}
                  char={ch}
                  palette={palettes[ch.paletteIndex]}
                  cardStyle={cardStyle}
                  cardFinish={cardFinish}
                  dimmed={hoveredSoloId !== null && hoveredSoloId !== ch.id}
                  onHoverChange={(isHovered) => {
                    if (isHovered) {
                      setHoveredSoloId(ch.id);
                    } else {
                      setHoveredSoloId(prev => prev === ch.id ? null : prev);
                    }
                  }}
                  onClick={() => {
                    if (ch.isDemo) {
                      actions?.onEnterDemoCampaign?.({
                        role: 'player',
                        charId: ch.charId || ch.id || DEFAULT_DEMO_PLAYER_CHAR_ID,
                      });
                    } else {
                      actions?.onEnterAsStandaloneChar?.(ch.id);
                    }
                  }}
                  onDelete={!ch.isDemo && actions?.onDeleteStandaloneCharacter
                    ? () => actions.onDeleteStandaloneCharacter(ch.charId || ch.id, ch.name)
                    : undefined}
                />
              ))}
              <NewCharCard
                theme={theme}
                density={density}
                cardStyle={cardStyle}
                onClick={() => actions?.onOpenCreateCharacter?.()}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="Announcements"
              count={enrichedNotes.length}
              theme={theme}
              density={density}
              action={
                <button style={{
                  background: 'none',
                  border: 'none',
                  color: c.accent,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontFamily: t.fontFamily,
                }}>See all →</button>
              }
            />
            {enrichedNotes.length === 0 ? (
              <div style={{
                padding: density.spacing.cardPadding,
                border: `1px dashed ${c.borderLight}`,
                borderRadius: theme.effects?.borderRadius || '8px',
                color: c.textMuted,
                fontStyle: 'italic',
                fontSize: '0.84rem',
                fontFamily: t.fontFamily,
                textAlign: 'center',
              }}>
                No announcements yet. DM announcements coming soon.
              </div>
            ) : (
              enrichedNotes.map(n => (
                <TableNote
                  key={n.id}
                  theme={theme}
                  density={density}
                  note={n}
                  palette={n.palette}
                />
              ))
            )}
          </div>

          <DemoCampaignCard
            theme={theme}
            density={density}
            palette={palettes[2]}
            onHoverChange={setDemoHovered}
            onOpenDemo={() => actions?.onOpenDemoCampaignSet?.()}
          />

          <div style={sectionRegionStyle}>
            <SectionHeader icon="✦" title="Get Started" theme={theme} density={density} />
            <GetStartedRow
              theme={theme} density={density}
              icon={<WizardHatIcon size={34} />}
              title="Create a character"
              body="New from scratch · or import from character sheet"
              palette={palettes[0]}
              onClick={() => actions?.onOpenCreateCharacter?.()}
            />
            <GetStartedRow
              theme={theme} density={density}
              icon={<BrandIcon size={34} />}
              title="Start a campaign"
              body="Own it as DM · transferable later"
              palette={palettes[3]}
              onClick={() => actions?.onOpenCreateCampaign?.()}
            />
            <GetStartedRow
              theme={theme} density={density}
              icon={<SpellbookIcon size={34} />}
              title="Join a campaign"
              body="Enter an invite code from your DM"
              palette={palettes[1]}
              onClick={() => actions?.onOpenJoinCampaign?.()}
            />
          </div>
        </div>
      </div>

      {/* Dev access — visible only on localhost or to the maintainer email
          allowlist; the component returns null for everyone else. */}
      <DevAccessLink onNavigate={() => actions?.onOpenDevDashboard?.()} />
    </>
  );
};
